import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Upload, X, Image as ImageIcon, Check, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const UploadArtwork = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: '',
    category: 'digital-art',
    medium: 'Digital',
    tools: '',
    mature_content: false
  });
  
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { value: 'digital-art', label: 'Digital Art' },
    { value: 'character-design', label: 'Character Design' },
    { value: 'concept-art', label: 'Concept Art' },
    { value: 'portrait', label: 'Portrait' },
    { value: 'environment', label: 'Environment' },
    { value: 'fan-art', label: 'Fan Art' },
    { value: 'original-character', label: 'Original Character' },
    { value: 'illustration', label: 'Illustration' },
    { value: 'study', label: 'Study/Practice' },
    { value: '3d-render', label: '3D Render' },
    { value: 'traditional', label: 'Traditional Art' },
    { value: 'pixel-art', label: 'Pixel Art' },
    { value: 'vector-art', label: 'Vector Art' }
  ];

  const mediums = ['Digital', 'Traditional', '3D', 'Mixed Media', 'Photography'];

  const handleFileChange = (e) => {
    // DO NOT call preventDefault/stopPropagation on file input onChange —
    // it can interfere with iOS Safari's native file selection pipeline.
    
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const selectedFiles = Array.from(e.target.files);
    
    // Reset input value so the same file can be re-selected
    e.target.value = '';
    
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    if (!newFiles || newFiles.length === 0) {
      return;
    }
    
    // Filter image files — be permissive for iPad Safari:
    // iOS HEIC files may have empty or 'application/octet-stream' MIME type
    const imageFiles = newFiles.filter(file => {
      const isImage = file.type.startsWith('image/');
      const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(file.name);
      // iOS sometimes sends empty type for camera photos
      const isLikelyImage = !file.type && file.size > 0;
      const accepted = isImage || hasImageExtension || isLikelyImage;
      if (!accepted) {
        console.warn(`[Artwork Upload] Rejected: ${file.name} (${file.type})`);
      }
      return accepted;
    });
    
    if (imageFiles.length === 0) {
      toast.error('No image files detected. Supported: JPG, PNG, GIF, HEIC');
      return;
    }

    // Limit to 10 images
    if (files.length + imageFiles.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    // Check file sizes (20MB max per file)
    const oversizedFiles = imageFiles.filter(file => file.size > 20 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed 20MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Show loading state
    setLoadingPreviews(true);
    toast.info(`Loading ${imageFiles.length} image(s)...`);

    // Add files to state first
    setFiles(prev => [...prev, ...imageFiles]);

    // Generate previews with better error handling
    let loadedCount = 0;
    const newPreviews = [];
    
    imageFiles.forEach((file, index) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        newPreviews[index] = e.target.result;
        loadedCount++;
        
        // Update previews state
        if (loadedCount === imageFiles.length) {
          setPreviews(prev => [...prev, ...newPreviews]);
          setLoadingPreviews(false);
          toast.success(`${imageFiles.length} image(s) ready to upload`);
        }
      };
      
      reader.onerror = (error) => {
        console.error('FileReader error for file:', file.name, error);
        toast.error(`Failed to load ${file.name}. Try a different image.`);
        loadedCount++;
        
        if (loadedCount === imageFiles.length) {
          setLoadingPreviews(false);
        }
      };
      
      reader.onabort = () => {
        console.error('FileReader aborted for file:', file.name);
        loadedCount++;
        
        if (loadedCount === imageFiles.length) {
          setLoadingPreviews(false);
        }
      };
      
      try {
        // Use readAsDataURL for preview generation
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error starting FileReader for file:', file.name, error);
        toast.error(`Error reading ${file.name}`);
        loadedCount++;
        
        if (loadedCount === imageFiles.length) {
          setLoadingPreviews(false);
        }
      }
    });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please sign in to upload artwork');
      navigate('/auth');
      return;
    }

    if (files.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setUploading(true);

    try {
      // Prepare form data
      const uploadData = new FormData();
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description);
      uploadData.append('tags', formData.tags);
      uploadData.append('category', formData.category);
      uploadData.append('medium', formData.medium);
      uploadData.append('tools', formData.tools);
      uploadData.append('mature_content', formData.mature_content);

      // Add files
      files.forEach(file => {
        uploadData.append('files', file);
      });

      // Upload
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/art/post`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh user data in case role was upgraded
      try {
        const userResponse = await axios.get(`${API}/users/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userResponse.data.role !== user.role) {
          const updatedUser = { ...user, role: userResponse.data.role };
          updateUser(updatedUser);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }

      toast.success('Artwork uploaded successfully!');
      navigate(`/art/${response.data.id}`);

    } catch (error) {
      console.error('[Artwork Upload] Upload error:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to upload';
      const status = error.response?.status || 'network error';
      toast.error(`Upload failed (${status}): ${detail}`);
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Card className="bg-gray-900 border-gray-800 max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Sign In Required</h2>
            <p className="text-gray-400 mb-6">Please sign in to upload artwork</p>
            <Button onClick={() => navigate('/auth')} className="bg-orange-500 hover:bg-orange-600">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black mb-2">Upload Artwork</h1>
          <p className="text-gray-400">Share your art with the Trap Comix community</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload Area */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <h3 className="text-xl font-bold">Images</h3>
              <p className="text-sm text-gray-400">Upload up to 10 images (JPG, PNG, GIF)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Drop Zone — iPad-safe: input overlays the entire area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg p-6 sm:p-12 text-center transition-colors ${
                  dragActive
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
                data-testid="artwork-dropzone"
              >
                {/* The file input covers the entire dropzone — user taps the actual <input>,
                    no label indirection needed. This is the only pattern that works
                    reliably on iPad Safari (same as FileUpload.js for Series/Novels). */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  data-testid="artwork-file-input"
                />
                <div className="pointer-events-none">
                  <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500 mx-auto mb-3 sm:mb-4" />
                  <p className="text-base sm:text-lg font-medium mb-2 text-white">
                    {previews.length > 0 ? 'Add more images' : 'Tap to select images'}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Max 10 images, up to 20MB each
                    <br />
                    <span className="text-xs text-gray-600">JPG, PNG, GIF, HEIC supported</span>
                  </p>
                </div>
              </div>

              {/* Preview Grid */}
              {(files.length > 0 || loadingPreviews) && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-gray-400">
                      {loadingPreviews ? 'Loading previews...' : `${files.length} image(s) selected`}
                    </p>
                    {files.length > 0 && !loadingPreviews && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFiles([]);
                          setPreviews([]);
                        }}
                        className="text-red-500 hover:text-red-400"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                  
                  {loadingPreviews && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    </div>
                  )}
                  
                  {!loadingPreviews && previews.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {previews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full aspect-square object-cover rounded-lg border-2 border-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {index === 0 && (
                            <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded shadow-lg">
                              Cover
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <h3 className="text-xl font-bold">Details</h3>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Give your artwork a title"
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Tell us about your artwork..."
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={4}
                />
              </div>

              {/* Category and Medium */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="medium">Medium</Label>
                  <select
                    id="medium"
                    value={formData.medium}
                    onChange={(e) => setFormData({...formData, medium: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                  >
                    {mediums.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="portrait, fantasy, character, etc. (comma separated)"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {/* Tools */}
              <div>
                <Label htmlFor="tools">Tools/Software Used</Label>
                <Input
                  id="tools"
                  value={formData.tools}
                  onChange={(e) => setFormData({...formData, tools: e.target.value})}
                  placeholder="Procreate, Photoshop, Blender, etc. (comma separated)"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {/* Mature Content */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mature"
                  checked={formData.mature_content}
                  onChange={(e) => setFormData({...formData, mature_content: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="mature" className="cursor-pointer">
                  This artwork contains mature content (18+)
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(-1)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600"
              disabled={uploading || files.length === 0}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Upload Artwork
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadArtwork;
